<template>
  <div class="relative text-yapgray-500">
    <label
      v-if="!noLabel && !isFloatingLabel"
      :for="id ? id : name"
      class="left-2 z-20"
      :class="{
        'required-label': !!required,
        'text-xs text-yapgray-600 dark:text-yapgray-400': !boldLabel,
        'block text-sm font-medium leading-6 text-yapgray-900 dark:text-yapgray-500': boldLabel
      }"
    >
      {{ label ? label : schema.title }}
    </label>
    <div class="relative flex w-full items-center">
      <YapIcon v-if="leftIcon" :name="leftIcon" class="pointer-events-none absolute z-20 ml-3 h-5 w-5" />
      <input
        v-if="!isTextArea && !isSelect && !['boolean', 'array'].includes(type)"
        v-model="modelValue"
        :id="id ? id : name"
        :type="type"
        :name="name"
        :maxlength="schema.maxLength"
        :minlength="schema.minLength"
        :min="min || schema.minimum"
        :max="max || schema.maximum"
        :required="required"
        :placeholder="internalPlaceholder"
        :readonly="readonly || schema.readonly"
        v-bind="$attrs"
        :accept="accept"
        :autocomplete="autocomplete"
        :step="schema.type === 'integer' ? 1 : undefined"
        class="peer w-full overflow-visible border-yapgray-300 text-yapgray-800 focus:z-10 focus:border-primary-500 focus:ring-primary-500 dark:border-yapdarkgray-500 dark:bg-yapdarkgray-800 dark:text-yapgray-100 dark:color-scheme-dark dark:focus:border-primary-dark-400 dark:focus:ring-primary-dark-400"
        :class="{
          'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200': readonly || schema.readonly,
          'rounded-lg shadow-sm': !grouped,
          'border-0': grouped,
          'h-14': floating,
          'border-yapred-500 focus:border-yapred-500 focus:ring-yapred-500 dark:border-yapred-500 dark:focus:border-yapred-500 dark:focus:ring-yapred-500':
            error,
          'pl-10': !!leftIcon,
          'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
          'dark:placeholder-yapdarkgray-300': !floating,
          uppercase: props.uppercase
        }"
        :data-testid="`yap-input-${id ? id : name}`"
        @input="updateValue"
        @blur="$emit('blur')"
      />
      <textarea
        v-else-if="isTextArea && typeof modelValue === 'string' && !['boolean', 'array'].includes(type) && !isSelect"
        :id="id ? id : name"
        :name="name"
        v-model="modelValue"
        :maxlength="schema.maxLength"
        :minlength="schema.minLength"
        :required="required"
        :placeholder="internalPlaceholder"
        class="peer w-full border-yapgray-300 text-yapgray-800 focus:border-primary-500 focus:ring-primary-500 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-800 dark:text-yapgray-100 focus:dark:border-primary-dark-500 focus:dark:ring-primary-dark-500"
        :class="{
          'rounded-lg shadow-sm': !grouped,
          'border-0': grouped,
          'pl-10': !!leftIcon,
          'border-yapred-500 focus:border-yapred-500 focus:ring-yapred-500 dark:border-yapred-500 dark:focus:border-yapred-500 dark:focus:ring-yapred-500':
            error,
          'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
          'dark:placeholder-yapdarkgray-300': !floating
        }"
        v-bind="$attrs"
        :data-testid="`yap-input-textarea-${id ? id : name}`"
        @input="updateValue"
        @blur="$emit('blur')"
      ></textarea>
      <YapSelect
        v-else-if="isSelect"
        :id="id ? id : name"
        :name="name"
        :schema="schema"
        :required="required"
        v-model="modelValue"
        class="w-full border-yapgray-300 text-yapgray-800 focus:border-primary-500 focus:ring-primary-500 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-800 dark:text-yapgray-100 focus:dark:border-primary-dark-500 focus:dark:ring-primary-dark-500"
        :class="{
          'rounded-lg shadow-sm': !grouped,
          'border-0': grouped,
          'h-14': !error && floating,
          'pl-10': !!leftIcon,
          'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
          'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200': readonly || schema.readonly
        }"
        v-bind="$attrs"
        :readonly="readonly"
        :data-testid="`yap-input-select-${id ? id : name}`"
        @input="updateValue"
        @blur="$emit('blur')"
      />
      <Switch
        v-else-if="type === 'boolean' && typeof modelValue === 'boolean'"
        :id="id ? id : name"
        v-model="modelValue"
        :class="[
          modelValue ? 'bg-yapmteal-500 dark:bg-yapmteal-700' : 'bg-yapgray-400 dark:bg-yapdarkgray-500',
          !!leftIcon ? 'ml-10' : ''
        ]"
        class="relative inline-flex h-6 w-11 items-center rounded-full"
        :required="required"
        v-bind="$attrs"
        :data-testid="`yap-input-switch-${id ? id : name}`"
        @on-change="updateValue"
        @input="updateValue"
        @blur="$emit('blur')"
      >
        <span
          :class="modelValue ? 'translate-x-6' : 'translate-x-1'"
          class="inline-block h-4 w-4 transform rounded-full bg-white dark:bg-yapdarkgray-200"
        />
      </Switch>

      <YapMultiSelect
        v-else-if="type === 'array'"
        :id="id ? id : name"
        :name="name"
        :schema="schema"
        :required="required"
        v-model="modelValue"
        :floating="floating"
        :grouped="grouped"
        :class="{
          'rounded-lg border-yapgray-300 shadow-sm': !grouped,
          'pl-8': !!leftIcon
        }"
        v-bind="$attrs"
        @input="updateValue"
        @blur="$emit('blur')"
      />

      <pre v-else>--- {{ type }}: not supported yet---</pre>
      <div class="absolute ml-3 flex items-center" :class="[isSelect ? 'right-8' : 'right-2']">
        <YapIcon v-if="rightIcon" :name="rightIcon" class="pointer-events-none absolute z-20 -ml-5 w-5" />
        <YapTooltip :text="hint" class="w-56" v-if="!!hint">
          <YapIcon
            name="info-outline"
            class="w-5 cursor-pointer text-yapgray-500 hover:text-primary-500 dark:hover:text-primary-dark-300"
            @click="resetInput"
          />
        </YapTooltip>
        <YapIcon
          v-if="clearable && modelValue !== undefined && modelValue !== ''"
          name="close-outline"
          class="z-30 w-5 cursor-pointer hover:text-primary-500 dark:hover:text-primary-dark-300"
          @click="resetInput"
        />
      </div>

      <label
        v-if="!noLabel && isFloatingLabel"
        :for="id ? id : name"
        class="absolute top-0.5 z-20 block w-full text-xs text-yapgray-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-yapgray-500 peer-focus:top-0.5 peer-focus:text-xs peer-focus:text-primary-500 dark:peer-placeholder-shown:text-yapgray-500 peer-focus:dark:text-primary-dark-300"
        :class="[!!leftIcon ? 'pl-10' : 'left-2']"
      >
        <span
          class="w-full pb-1 pr-4"
          :class="{
            'text-yapgray-600 dark:text-yapgray-200': readonly || schema.readonly,
            'bg-white/80 dark:bg-yapdarkgray-800/80': isTextArea && !schema.readonly,
            'bg-yapgray-200 dark:bg-yapdarkgray-700': isTextArea && schema.readonly,
            'required-label': !!required
          }"
          >{{ label ? label : schema.title }}</span
        >
      </label>
      <div v-if="isFloatingLabel && !state.inputHasChangedSinceError" class="absolute bottom-0 z-20">
        <YapInputError
          v-if="floating"
          :error="error"
          class="xmt-0 z-20 text-sm"
          :class="[!!leftIcon ? 'pl-10' : 'pl-2']"
        />
      </div>
    </div>
    <YapInputError v-if="!isFloatingLabel && !state.inputHasChangedSinceError" :error="error" class="mt-0" />
  </div>
</template>

<script setup lang="ts">
import { Switch } from '@headlessui/vue';
import { computed, reactive, watch, onMounted } from 'vue';

import YapIcon from '@/components/YapIcon.vue';
import YapTooltip from '@/components/YapTooltip.vue';
import YapSelect from '@/components/form/YapSelect.vue';
import { SchemaItem } from '@/components/form/types/schemaItem';
import YapInputError from '@/components/form/YapInputError.vue';
import YapMultiSelect from '@/components/form/YapMultiSelect.vue';
import useInputValidation from '@/composables/useInputValidation';
import { formatIsoToLocaleString, LocaleFormatType } from '@/utils/locale';
import { YapInputValidationError } from '@/components/form/types/yapInputValidationError';

const modelValue = defineModel();

const props = defineProps<{
  name: string;
  schema: SchemaItem;
  floating?: boolean;
  label?: string;
  noLabel?: boolean;
  required?: boolean;
  id?: string;
  accept?: string;
  errors?: Array<YapInputValidationError>;
  boldLabel?: boolean;
  inputType?: 'text' | 'textarea';
  min?: string | number;
  max?: string | number;
  placeholder?: string;
  leftIcon?: string;
  rightIcon?: string;
  grouped?: boolean;
  clearable?: boolean;
  hint?: string;
  readonly?: boolean; // Allows to force readonly and bypass what's defined in the schema
  autocomplete?: string;
  uppercase?: boolean;
}>();

const emit = defineEmits(['update:modelValue', 'blur']);

onMounted(() => {
  if (props.schema.default) {
    emit('update:modelValue', props.schema.default);
  }
});

/**
 * Regardless of floating props, in case of switch we do not want any floating label
 */
const isFloatingLabel = computed(
  () => props.floating && type.value != 'boolean' && typeof modelValue.value !== 'boolean'
);

const isTextArea = computed(() => {
  return !!props.schema.maxLength && props.schema.maxLength > 200 && (!props.inputType || props.inputType !== 'text');
});

const isSelect = computed(() => {
  return !!props.schema.enum;
});

const type = computed(() => {
  switch (props.schema.type) {
    case 'string':
      if (props.schema.format === 'date') {
        return 'date';
      } else if (props.schema.format === 'email') {
        return 'email';
      } else {
        return 'text';
      }
    case 'integer':
      return 'number';
    case 'number':
    case 'boolean':
    case 'array':
      return props.schema.type;

    default:
      return 'text';
  }
});

const internalPlaceholder = computed(() =>
  props.placeholder ? props.placeholder : props.floating ? 'placeholder' : undefined
);

interface State {
  localError?: YapInputValidationError;
  error?: YapInputValidationError;
  inputHasChangedSinceError: boolean;
}

const { filterErrorFor, validateSchemaPattern } = useInputValidation();
const state: State = reactive({
  localError: undefined,
  error: computed(() => (props.errors ? filterErrorFor(props.name, props.errors) : undefined)),
  inputHasChangedSinceError: false
});

function resetInput() {
  modelValue.value = '';
  emit('update:modelValue', '');
}

const error = computed(() => {
  if (!!state.error) {
    return state.error;
  } else if (!!state.localError) {
    return state.localError;
  }
  return undefined;
});

watch(
  () => props.errors,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      state.inputHasChangedSinceError = false;
    }
  }
);

function updateValue(event: Event) {
  // reset localError on user input
  state.localError = undefined;
  state.inputHasChangedSinceError = true;

  const input = event.target as HTMLInputElement;
  if (!input) {
    /**
     * In case of multiselect we do not receive an event but the array of selected items.
     * We can skip the validation of those as the user should only be able to
     * select from the available list of items.
     */
    return;
  }
  if (props.required && input.value !== '' && !validateSchemaPattern(props.schema, input.value ?? event)) {
    state.localError = {
      keyword: 'pattern',
      params: {
        comparison: props.schema.pattern
      }
    };
  } else if (!input.checkValidity()) {
    const validityState = input.validity;
    if (validityState.badInput) {
      state.localError = {
        keyword: 'badInput',
        params: {}
      };
    } else if (validityState.patternMismatch) {
      state.localError = {
        keyword: 'pattern',
        params: {
          limit: props.schema.maximum
        }
      };
    } else if (validityState.rangeOverflow) {
      state.localError = {
        keyword: props.schema.format === 'date' ? 'maximumDate' : 'maximum',
        params: {
          limit:
            props.schema.format === 'date'
              ? formatIsoToLocaleString(`${props.max}`, LocaleFormatType.DateShort)
              : props.schema.maximum
        }
      };
    } else if (validityState.rangeUnderflow) {
      state.localError = {
        keyword: props.schema.format === 'date' ? 'minimumDate' : 'minimum',
        params: {
          limit:
            props.schema.format === 'date'
              ? formatIsoToLocaleString(`${props.min}`, LocaleFormatType.DateShort)
              : props.schema.minimum
        }
      };
    } else if (validityState.stepMismatch) {
      state.localError = {
        keyword: 'badInput',
        params: {}
      };
    } else if (validityState.tooLong) {
      state.localError = {
        keyword: 'maxLength',
        params: {
          limit: props.schema.maxLength
        }
      };
    } else if (validityState.tooShort) {
      state.localError = {
        keyword: 'minLength',
        params: {
          limit: props.schema.minLength
        }
      };
    } else if (validityState.typeMismatch) {
      state.localError = {
        keyword: 'type',
        params: {
          type: props.schema.type
        }
      };
    } else if (validityState.valueMissing) {
      state.localError = {
        keyword: 'required',
        params: {}
      };
    }
  }
}
</script>

<style scoped>
.required-label:after {
  @apply ml-0.5 text-yapred-500;
  content: '*';
}
</style>
